import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSun, faMoon, faDesktop, faUser, faLifeRing,
    faCode, faRightFromBracket, faChevronRight, faChevronUp, faChevronDown,
    faMagnifyingGlass, faUserShield, faBoxesStacked,
} from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../../styles/theme/themeContext.jsx';
import { UserAuth } from '../../authentication/AuthContext.jsx';
import UserAvatar from '../../utils/UserAvatar.jsx';
import { CHEATS_NAV_ITEMS } from '../../config/cheatsCatalog.jsx';
import { scopeLinks } from '../../routes/navScopes.jsx';
import { getFirstAccessiblePath } from '../../utils/rbacUtils.js';
import { updateDashboardContext } from '../../redux-store/ReduxStore.js';
import './HelpPopupButton.css';

// Developer Tools flyout entries — derived from the single cheats catalog.
// `category` groups them into inline section labels within the flyout's
// scrollable list, same grouping as the sidebar (cheatsNavSubItems).
const DEV_TOOLS = CHEATS_NAV_ITEMS.map(t => ({ label: t.label, path: t.path, category: t.category || 'Other' }));

/** Groups a flat, already-filtered list into [{category, items}] preserving first-seen order. */
function groupByCategory(items) {
    const order = [];
    const byCategory = new Map();
    items.forEach(item => {
        if (!byCategory.has(item.category)) {
            byCategory.set(item.category, []);
            order.push(item.category);
        }
        byCategory.get(item.category).push(item);
    });
    return order.map(category => ({ category, items: byCategory.get(category) }));
}

// Help sub-menu shown as a hover flyout next to the Help row. Kept in sync
// with the sidebar's Help section in navConfig.jsx — Docs deliberately opens
// in a new browser tab so users can keep the reference window open alongside
// the main app.
const HELP_ITEMS = [
    { label: 'Support', path: '/help/support' },
    { label: 'Docs', path: '/help/docs', openInNewTab: true },
];

// Sibling of HELP_ITEMS — the GeoPlay release feed. Kept in sync with the Releases section in
// navConfig.jsx. The SDK page is the download centre for the client packages; Settings → SDK
// Integration shows the same feed narrowed to one game's platforms.
const RELEASE_ITEMS = [
    { label: 'APIs Release', path: '/releases/apis' },
    { label: 'Client SDKs', path: '/releases/sdks' },
];

const getSystemTheme = () =>
    (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches)
        ? 'dark' : 'light';

export const HelpPopupButton = ({ expanded = false }) => {
    const buttonRef = useRef(null);
    const popupRef = useRef(null);
    const tooltipTimerRef = useRef(null);
    const devToolsBtnRef = useRef(null);
    const devToolsTimerRef = useRef(null);
    const devToolsFlyoutRef = useRef(null);
    const helpBtnRef = useRef(null);
    const helpTimerRef = useRef(null);
    const releasesBtnRef = useRef(null);
    const releasesTimerRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);
    const [showDevTools, setShowDevTools] = useState(false);
    const [devToolsPos, setDevToolsPos] = useState(null);
    const [devToolsQuery, setDevToolsQuery] = useState('');
    const [showHelp, setShowHelp] = useState(false);
    const [helpPos, setHelpPos] = useState(null);
    const [showReleases, setShowReleases] = useState(false);
    const [releasesPos, setReleasesPos] = useState(null);
    const [appearance, setAppearance] = useState(() => localStorage.getItem('app-appearance') || 'system');

    const { setTheme } = useTheme();
    const { user, logOut } = UserAuth();
    // Super users have no App Settings (no landing-page/workspace prefs — their control plane is fixed).
    const isSuperUser = useSelector(state => state.userDetails?.superUser);
    // Authoritative display name — the backend userName the user sets (and sees)
    // on the Profile page's General Settings. Firebase displayName / email are
    // only fallbacks, so this label always matches what's shown there.
    const reduxUserName = useSelector(state => state.userDetails?.userName);
    const userRole = useSelector(state => state.dashboardContext?.userRole);
    const effectivePermissions = useSelector(state => state.effectivePermissions);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const userName = reduxUserName || user?.displayName || user?.email || 'Account';
    // For initials we want the human's name (not an email) so the fallback
    // tile reads as "SV" rather than "S" for an email-only login. When we have
    // no human name we deliberately leave it blank — UserAvatar then renders
    // '?' which is the right "unknown user" affordance.
    const avatarSourceName = reduxUserName || user?.displayName || '';

    useEffect(() => {
        if (!isOpen) return;
        const onDocClick = (e) => {
            if (
                popupRef.current && !popupRef.current.contains(e.target) &&
                buttonRef.current && !buttonRef.current.contains(e.target)
            ) {
                setIsOpen(false);
                setShowDevTools(false);
                setShowHelp(false);
                setShowReleases(false);
            }
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [isOpen]);

    useEffect(() => {
        if (appearance === 'system') {
            const mq = window.matchMedia('(prefers-color-scheme: dark)');
            const apply = () => setTheme(mq.matches ? 'dark' : 'light');
            apply();
            mq.addEventListener('change', apply);
            return () => mq.removeEventListener('change', apply);
        }
        setTheme(appearance);
    }, [appearance, setTheme]);

    const chooseAppearance = (mode) => {
        setAppearance(mode);
        localStorage.setItem('app-appearance', mode);
    };

    const handleMouseEnter = () => {
        tooltipTimerRef.current = setTimeout(() => setShowTooltip(true), 300);
    };
    const handleMouseLeave = () => {
        clearTimeout(tooltipTimerRef.current);
        setShowTooltip(false);
    };

    const goAndClose = (path) => {
        navigate(path);
        setIsOpen(false);
        setShowDevTools(false);
        setShowHelp(false);
        setShowReleases(false);
    };

    // Where "Admin Settings" lands: the first admin page this user can actually open. Null when they
    // hold none of them, which is also what hides the row — the nodes' pagePaths are the only gate.
    const adminLanding = useMemo(() => {
        const path = getFirstAccessiblePath(
            scopeLinks('admin'), userRole, effectivePermissions);
        return path === '/not-authorized' ? null : path;
    }, [userRole, effectivePermissions]);

    const enterAdminScope = () => {
        dispatch(updateDashboardContext({ navScope: 'admin' }));
        goAndClose(adminLanding);
    };

    // Dispatches a sub-menu item — routes in-app or pops a new tab based on
    // the item's openInNewTab flag. Used for DEV_TOOLS, HELP_ITEMS, and
    // RELEASES_ITEMS.
    const handleSubItemClick = (item) => {
        if (item.openInNewTab) {
            window.open(item.path, '_blank', 'noopener,noreferrer');
            setIsOpen(false);
            setShowDevTools(false);
            setShowHelp(false);
            setShowReleases(false);
            return;
        }
        goAndClose(item.path);
    };

    const handleLogout = async () => {
        setIsOpen(false);
        // Navigate off the protected route before clearing auth state, otherwise
        // ProtectedRoute sees user=null and redirects to /not-authorized.
        navigate('/home');
        if (typeof logOut === 'function') await logOut();
    };

    const openDevTools = () => {
        clearTimeout(devToolsTimerRef.current);
        if (devToolsBtnRef.current) {
            const r = devToolsBtnRef.current.getBoundingClientRect();
            const margin = 16;
            // Taller than the Help flyout (HARD_MAX 260) — this one carries a
            // search box plus ~25 grouped tools, so it needs the extra room.
            const HARD_MAX = 340;
            // Flyout's bottom edge aligns with the row's bottom edge and it
            // grows upward. Cap at a fixed 360px, but shrink further if the
            // room above the row is smaller than that.
            setDevToolsPos({
                left: r.right + 8,
                bottom: window.innerHeight - r.bottom,
                maxHeight: Math.min(HARD_MAX, r.bottom - margin),
            });
        }
        setShowDevTools(true);
    };

    const scheduleCloseDevTools = () => {
        clearTimeout(devToolsTimerRef.current);
        devToolsTimerRef.current = setTimeout(() => {
            // Typing into the search box re-renders the filtered list, which can shrink/remove
            // the row the pointer was resting on and fire a spurious mouseleave on this
            // container even though the mouse never actually left it. Don't close while focus
            // is still inside the flyout (e.g. the search input) — only a real pointer-out or
            // blur should close it.
            if (devToolsFlyoutRef.current?.contains(document.activeElement)) return;
            setShowDevTools(false);
            setDevToolsQuery('');
        }, 150);
    };

    const cancelCloseDevTools = () => {
        clearTimeout(devToolsTimerRef.current);
    };

    // Mirrors the Developer Tools flyout behaviour — see openDevTools above
    // for the positioning rationale (flyout aligns to the row and grows up).
    const openHelp = () => {
        clearTimeout(helpTimerRef.current);
        if (helpBtnRef.current) {
            const r = helpBtnRef.current.getBoundingClientRect();
            const margin = 16;
            const HARD_MAX = 260;
            setHelpPos({
                left: r.right + 8,
                bottom: window.innerHeight - r.bottom,
                maxHeight: Math.min(HARD_MAX, r.bottom - margin),
            });
        }
        setShowHelp(true);
    };

    const scheduleCloseHelp = () => {
        clearTimeout(helpTimerRef.current);
        helpTimerRef.current = setTimeout(() => setShowHelp(false), 150);
    };

    const cancelCloseHelp = () => {
        clearTimeout(helpTimerRef.current);
    };

    // Same flyout behaviour as Help above.
    const openReleases = () => {
        clearTimeout(releasesTimerRef.current);
        if (releasesBtnRef.current) {
            const r = releasesBtnRef.current.getBoundingClientRect();
            const margin = 16;
            const HARD_MAX = 260;
            setReleasesPos({
                left: r.right + 8,
                bottom: window.innerHeight - r.bottom,
                maxHeight: Math.min(HARD_MAX, r.bottom - margin),
            });
        }
        setShowReleases(true);
    };

    const scheduleCloseReleases = () => {
        clearTimeout(releasesTimerRef.current);
        releasesTimerRef.current = setTimeout(() => setShowReleases(false), 150);
    };

    const cancelCloseReleases = () => {
        clearTimeout(releasesTimerRef.current);
    };

    const popupPosition = () => {
        if (!buttonRef.current) return null;
        const r = buttonRef.current.getBoundingClientRect();
        return { left: r.left, bottom: window.innerHeight - r.top + 8 };
    };
    const pos = isOpen ? popupPosition() : null;

    return (
        <div className={`help-popup-root ${expanded ? 'expanded' : 'collapsed'}`}>
            <button
                ref={buttonRef}
                className={`help-popup-trigger ${expanded ? 'expanded' : 'collapsed'}`}
                onClick={() => setIsOpen(v => !v)}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                aria-label={expanded ? userName : 'Account & preferences'}
            >
                <span className="help-popup-trigger-avatar">
                    <UserAvatar
                        imageUrl={user?.photoURL}
                        userName={avatarSourceName}
                        size={22}
                        numChars={2}
                        showNameOnHover={false}
                    />
                </span>
                {expanded && <span className="help-popup-trigger-label">{userName}</span>}
                {expanded && (
                    <FontAwesomeIcon
                        icon={isOpen ? faChevronDown : faChevronUp}
                        className="help-popup-trigger-caret"
                    />
                )}
            </button>

            {showTooltip && !isOpen && !expanded && buttonRef.current && createPortal(
                <div
                    className="help-tooltip"
                    style={{
                        position: 'fixed',
                        left: buttonRef.current.getBoundingClientRect().right + 10,
                        top: buttonRef.current.getBoundingClientRect().top +
                             buttonRef.current.getBoundingClientRect().height / 2,
                        transform: 'translateY(-50%)',
                    }}
                >
                    {userName}
                </div>,
                document.body
            )}

            {isOpen && pos && createPortal(
                <div
                    ref={popupRef}
                    className="help-popup"
                    style={{ position: 'fixed', left: pos.left, bottom: pos.bottom }}
                >
                    <div className="help-popup-section-label">Appearance</div>

                    <div className="help-popup-appearance" role="radiogroup" aria-label="Appearance">
                        {[
                            { key: 'light', icon: faSun, label: 'Light' },
                            { key: 'dark', icon: faMoon, label: 'Dark' },
                            { key: 'system', icon: faDesktop, label: 'System' },
                        ].map(opt => (
                            <button
                                key={opt.key}
                                className={`help-popup-appearance-btn ${appearance === opt.key ? 'active' : ''}`}
                                onClick={() => chooseAppearance(opt.key)}
                                aria-label={opt.label}
                                aria-pressed={appearance === opt.key}
                            >
                                <FontAwesomeIcon icon={opt.icon} />
                            </button>
                        ))}
                    </div>

                    <div className="help-popup-menu">
                        {/* Entering the admin scope focuses the sidebar on the admin sections. Hidden
                            when the user holds none of them — adminLanding resolves to the first
                            page they can actually open, or null. */}
                        {adminLanding && (
                            <button className="help-popup-row" onClick={enterAdminScope}>
                                <FontAwesomeIcon icon={faUserShield} className="help-popup-row-icon" />
                                <span>Admin Settings</span>
                            </button>
                        )}
                        {/* Developer Tools are super-user-only (matches the route + backend cheats gate). */}
                        {isSuperUser && (
                        <button
                            ref={devToolsBtnRef}
                            className={`help-popup-row ${showDevTools ? 'active' : ''}`}
                            onMouseEnter={openDevTools}
                            onMouseLeave={scheduleCloseDevTools}
                            onClick={openDevTools}
                        >
                            <FontAwesomeIcon icon={faCode} className="help-popup-row-icon" />
                            <span>Developer Tools</span>
                            <FontAwesomeIcon icon={faChevronRight} className="help-popup-row-arrow" />
                        </button>
                        )}
                        {/* Releases, then Profile, then Help — the order of the bottom sections in navConfig. */}
                        <button
                            ref={releasesBtnRef}
                            className={`help-popup-row ${showReleases ? 'active' : ''}`}
                            onMouseEnter={openReleases}
                            onMouseLeave={scheduleCloseReleases}
                            onClick={openReleases}
                        >
                            <FontAwesomeIcon icon={faBoxesStacked} className="help-popup-row-icon" />
                            <span>Releases</span>
                            <FontAwesomeIcon icon={faChevronRight} className="help-popup-row-arrow" />
                        </button>
                        <button className="help-popup-row" onClick={() => goAndClose('/profile')}>
                            <FontAwesomeIcon icon={faUser} className="help-popup-row-icon" />
                            <span>Profile</span>
                        </button>
                        <button
                            ref={helpBtnRef}
                            className={`help-popup-row ${showHelp ? 'active' : ''}`}
                            onMouseEnter={openHelp}
                            onMouseLeave={scheduleCloseHelp}
                            onClick={openHelp}
                        >
                            <FontAwesomeIcon icon={faLifeRing} className="help-popup-row-icon" />
                            <span>Help</span>
                            <FontAwesomeIcon icon={faChevronRight} className="help-popup-row-arrow" />
                        </button>
                    </div>

                    <div className="help-popup-divider" />
                    <button className="help-popup-row help-popup-row--danger" onClick={handleLogout}>
                        <FontAwesomeIcon icon={faRightFromBracket} className="help-popup-row-icon" />
                        <span>Logout</span>
                    </button>

                    {showDevTools && devToolsPos && (() => {
                        const q = devToolsQuery.trim().toLowerCase();
                        // Matches on the tool's own label OR its section/category name — typing "AI"
                        // pulls in every tool under the "AI Tools" group, not just ones literally
                        // named "AI something".
                        const filtered = q
                            ? DEV_TOOLS.filter(t =>
                                t.label.toLowerCase().includes(q) || t.category.toLowerCase().includes(q))
                            : DEV_TOOLS;
                        const groups = groupByCategory(filtered);
                        return (
                            <div
                                ref={devToolsFlyoutRef}
                                className="help-popup-devtools"
                                onMouseEnter={cancelCloseDevTools}
                                onMouseLeave={scheduleCloseDevTools}
                                style={{
                                    position: 'fixed',
                                    left: devToolsPos.left,
                                    bottom: devToolsPos.bottom,
                                    // Fixed height (not just a cap) so filtering down to a handful of
                                    // results doesn't shrink-wrap the flyout to their combined height —
                                    // it stays the same size, with the list container filling the rest.
                                    height: devToolsPos.maxHeight,
                                }}
                            >
                                <div className="help-popup-devtools-search">
                                    <FontAwesomeIcon icon={faMagnifyingGlass} className="help-popup-devtools-search-icon" />
                                    <input
                                        type="text"
                                        className="help-popup-devtools-search-input"
                                        placeholder="Search tools…"
                                        value={devToolsQuery}
                                        onChange={(e) => setDevToolsQuery(e.target.value)}
                                        onClick={(e) => e.stopPropagation()}
                                        onFocus={cancelCloseDevTools}
                                        onBlur={scheduleCloseDevTools}
                                        autoFocus
                                    />
                                </div>
                                <div className="help-popup-devtools-list">
                                    {groups.length === 0 && (
                                        <div className="help-popup-devtools-empty">No tools match "{devToolsQuery}".</div>
                                    )}
                                    {groups.map(({ category, items }) => (
                                        <div key={category} className="help-popup-devtools-group">
                                            <div className="help-popup-devtools-group-label">{category}</div>
                                            {items.map(t => (
                                                <button
                                                    key={t.path}
                                                    className="help-popup-row help-popup-row--nested"
                                                    onClick={() => handleSubItemClick(t)}
                                                >
                                                    <span>{t.label}</span>
                                                </button>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })()}

                    {showReleases && releasesPos && (
                        <div
                            className="help-popup-devtools"
                            onMouseEnter={cancelCloseReleases}
                            onMouseLeave={scheduleCloseReleases}
                            style={{
                                position: 'fixed',
                                left: releasesPos.left,
                                bottom: releasesPos.bottom,
                                maxHeight: releasesPos.maxHeight,
                            }}
                        >
                            <div className="help-popup-devtools-header">Releases</div>
                            <div className="help-popup-devtools-list">
                                {RELEASE_ITEMS.map(item => (
                                    <button
                                        key={item.path}
                                        className="help-popup-row help-popup-row--nested"
                                        onClick={() => handleSubItemClick(item)}
                                    >
                                        <span>{item.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {showHelp && helpPos && (
                        <div
                            className="help-popup-devtools"
                            onMouseEnter={cancelCloseHelp}
                            onMouseLeave={scheduleCloseHelp}
                            style={{
                                position: 'fixed',
                                left: helpPos.left,
                                bottom: helpPos.bottom,
                                maxHeight: helpPos.maxHeight,
                            }}
                        >
                            <div className="help-popup-devtools-header">Help</div>
                            <div className="help-popup-devtools-list">
                                {HELP_ITEMS.map(item => (
                                    <button
                                        key={item.path}
                                        className="help-popup-row help-popup-row--nested"
                                        onClick={() => handleSubItemClick(item)}
                                    >
                                        <span>{item.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>,
                document.body
            )}
        </div>
    );
};
